import { Col, Row, Space, Statistic } from "antd";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { dashboardAreaData } from "./data/dashboardAreaData";
import { dashboardDossierColumns } from "./data/dashboardDossierColumns";
import { dashboardTasksData } from "./data/dashboardTasksData";
import { dashboardTasksColumns } from "./data/dashboardTasksColumns";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchDossiers } from "../../../lib/api/resources/dossiers";
import { Table } from "@/components/v2";
import { Button, Card } from "@rumbledirect/ui";

const formatCurrency = (value: number) => `€ ${value / 1000}k`;

const ClaimsAreaTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]; // using first series (uv)
  const value: number = point?.value ?? 0;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e5e5",
        padding: 8,
        borderRadius: 4,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14 }}>
        Kosten:{" "}
        <strong>
          {new Intl.NumberFormat("nl-NL", {
            style: "currency",
            currency: "EUR",
          }).format(value)}
        </strong>
      </div>
    </div>
  );
};

export const ClaimsDashboard = () => {
  const dossiersQuery = useQuery({
    queryKey: ["dossiers", "most-recent"],
    queryFn: fetchDossiers,
  });

  return (
    <Row gutter={[16, 32]} align="stretch">
      <Col span={6}>
        <Card>
          <Statistic
            title="Aantal openstaande claims"
            value={132}
            className="font-medium"
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="Gemiddelde doorlooptijd"
            value={24}
            suffix="dagen"
            className="font-medium"
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="Totale claimkosten afgelopen 30 dagen"
            value={187543}
            prefix="€"
            precision={2}
            className="font-medium"
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="Expertise benodigd"
            value={35}
            precision={1}
            suffix="%"
            className="font-medium"
          />
        </Card>
      </Col>
      <Col span={12}>
        <Card title="Claimkosten per maand">
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                width={500}
                height={400}
                data={dashboardAreaData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip content={<ClaimsAreaTooltip />} />
                <Area
                  type="monotone"
                  dataKey="uv"
                  stroke="#F76B0E"
                  fill="#F76B0E"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </Col>
      <Col span={12}>
        <Card style={{ height: "100%" }} title="Openstaande taken">
          <Table
            size="small"
            dataSource={dashboardTasksData}
            columns={dashboardTasksColumns}
            pagination={false}
          />
        </Card>
      </Col>
      <Col span={18}>
        <Card style={{ height: "100%" }} title="Laatst toegevoegde dossiers">
          <Table
            size="small"
            loading={dossiersQuery.isLoading}
            dataSource={dossiersQuery.data?.member.slice(0, 5) ?? []}
            columns={dashboardDossierColumns}
            pagination={false}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card style={{ height: "100%" }} title="Snelle acties">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Link to="/claims/dossiers">
              <Button block>Bekijk alle dossiers</Button>
            </Link>
            <Link to="/claims/kanban">
              <Button block>Ga naar status planbord</Button>
            </Link>
          </Space>
        </Card>
      </Col>
    </Row>
  );
};
